import React, { useState } from 'react';
import { Lock, ShieldCheck, CheckCircle2, AlertCircle } from 'lucide-react';

/**
 * Locked preview shown after analysis completes but before payment.
 * Displays just enough metadata to prove the report exists (ticker, audit verdict,
 * argument counts) plus a blurred placeholder skeleton — never the actual content.
 */
const LockedPreview = ({ teaser, onUnlock, unlocking, unlockError }) => {
  if (!teaser) return null;

  const isBulls = (teaser.audit_verdict || '').toUpperCase() === 'BULLS';
  const verdictColor = isBulls
    ? 'hsl(var(--gb-green-dark))'
    : 'hsl(var(--gb-bear-border))';
  const verdictBg = isBulls
    ? 'hsl(var(--gb-bull-surface))'
    : 'hsl(var(--gb-bear-surface))';
  const verdictEmoji = isBulls ? '🐂' : '🐻';

  const margin = Math.abs((teaser.audit_bulls_wins || 0) - (teaser.audit_bears_wins || 0));
  const marginLabel =
    margin >= 10 ? 'Dominant' : margin >= 6 ? 'Clear' : margin >= 3 ? 'Moderate' : 'Directional';

  const intro = Number(teaser.intro_price_usd || 1).toFixed(0);

  return (
    <section
      data-testid="locked-preview"
      className="rounded-lg border-2 overflow-hidden"
      style={{
        borderColor: 'hsl(var(--gb-gold))',
        backgroundColor: 'hsl(var(--gb-surface))',
      }}
    >
      {/* Top bar — analysis metadata */}
      <header
        className="px-5 py-4 border-b"
        style={{
          borderColor: 'hsl(var(--gb-border))',
          backgroundColor: 'hsl(var(--gb-surface-2))',
        }}
        data-testid="locked-preview-header"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div
              className="text-xs font-medium uppercase tracking-wider mb-1"
              style={{ color: 'hsl(var(--gb-text-muted))' }}
            >
              Report Generated
            </div>
            <div
              className="font-bold text-lg sm:text-xl"
              style={{
                color: 'hsl(var(--gb-navy))',
                fontFamily: 'var(--font-display)',
              }}
              data-testid="locked-preview-ticker"
            >
              <span style={{ color: 'hsl(var(--gb-gold))' }}>{teaser.ticker}</span>
              {teaser.company_name && teaser.company_name !== teaser.ticker ? (
                <span style={{ color: 'hsl(var(--gb-text-muted))', fontWeight: 400 }}>
                  {' '}
                  · {teaser.company_name}
                </span>
              ) : null}
            </div>
            <div
              className="text-sm mt-1 italic"
              style={{ color: 'hsl(var(--gb-text))' }}
              data-testid="locked-preview-event"
            >
              {teaser.event}
            </div>
          </div>
          <div className="text-right">
            <div
              className="text-xs uppercase tracking-wider"
              style={{ color: 'hsl(var(--gb-text-muted))' }}
            >
              {teaser.date}
            </div>
            <div
              className="mt-1 inline-flex items-center gap-1 text-xs font-medium"
              style={{ color: 'hsl(var(--gb-green-dark))' }}
            >
              <CheckCircle2 size={14} />
              Analysis & audit complete
            </div>
          </div>
        </div>
      </header>

      {/* Verdict callout */}
      <div className="px-5 py-5">
        <div
          className="rounded-md border px-4 py-4 flex items-center justify-between gap-4 flex-wrap"
          style={{
            borderColor: verdictColor,
            backgroundColor: verdictBg,
          }}
          data-testid="locked-preview-verdict"
        >
          <div className="flex items-center gap-3">
            <div className="text-3xl" aria-hidden="true">
              {verdictEmoji}
            </div>
            <div>
              <div
                className="text-xs uppercase tracking-wider font-semibold"
                style={{ color: verdictColor, opacity: 0.85 }}
              >
                Credibility Audit Verdict
              </div>
              <div
                className="text-2xl font-bold"
                style={{
                  color: verdictColor,
                  fontFamily: 'var(--font-display)',
                }}
                data-testid="locked-preview-verdict-name"
              >
                {teaser.audit_verdict || '—'} WIN
              </div>
            </div>
          </div>
          <div className="text-right">
            <div
              className="font-mono text-2xl font-bold"
              style={{ color: verdictColor }}
              data-testid="locked-preview-score"
            >
              {teaser.audit_bulls_wins}–{teaser.audit_bears_wins}
            </div>
            <div
              className="text-xs uppercase tracking-wider"
              style={{ color: verdictColor, opacity: 0.8 }}
            >
              {marginLabel} margin · {Number(teaser.audit_avg_decisiveness || 0).toFixed(2)}/3.0 decisiveness
            </div>
          </div>
        </div>

        {/* Counts row */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div
            className="rounded-md border px-4 py-3"
            style={{
              borderColor: 'hsl(var(--gb-bull-border))',
              backgroundColor: 'hsl(var(--gb-bull-surface))',
            }}
          >
            <div
              className="text-xs uppercase tracking-wider font-semibold"
              style={{ color: 'hsl(var(--gb-green-dark))' }}
            >
              Bull Arguments
            </div>
            <div
              className="text-2xl font-bold mt-0.5"
              style={{ color: 'hsl(var(--gb-green-dark))' }}
              data-testid="locked-preview-bulls-count"
            >
              {teaser.bulls_count}
            </div>
          </div>
          <div
            className="rounded-md border px-4 py-3"
            style={{
              borderColor: 'hsl(var(--gb-bear-border))',
              backgroundColor: 'hsl(var(--gb-bear-surface))',
            }}
          >
            <div
              className="text-xs uppercase tracking-wider font-semibold"
              style={{ color: 'hsl(var(--gb-bear-border))' }}
            >
              Bear Arguments
            </div>
            <div
              className="text-2xl font-bold mt-0.5"
              style={{ color: 'hsl(var(--gb-bear-border))' }}
              data-testid="locked-preview-bears-count"
            >
              {teaser.bears_count}
            </div>
          </div>
        </div>

        {/* Blurred placeholder cards — convey structure without leaking content */}
        <div className="mt-6 relative" aria-hidden="true">
          <div
            className="space-y-2 select-none pointer-events-none"
            style={{ filter: 'blur(6px)', opacity: 0.55 }}
          >
            {[
              'Executive summary line one packed with figures and dates.',
              'Executive summary line two drawing the through-line of the thesis.',
              'BULL #1 — institutional-grade evidence with primary-source citations.',
              'BEAR #1 — counter-thesis grounded in carve-outs and disclosure gaps.',
              'BULL #2 — capex-by-geography signal with year-over-year deltas.',
              'BEAR #2 — peer structural comparison with JV equity differential.',
            ].map((line, i) => (
              <div
                key={i}
                className="h-4 rounded"
                style={{
                  backgroundColor:
                    i % 2 === 0
                      ? 'hsl(var(--gb-bull-surface))'
                      : 'hsl(var(--gb-bear-surface))',
                  width: `${88 - (i % 3) * 6}%`,
                }}
              />
            ))}
            <div
              className="mt-3 grid grid-cols-2 gap-2"
            >
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="h-16 rounded"
                  style={{
                    backgroundColor: 'hsl(var(--gb-surface-2))',
                  }}
                />
              ))}
            </div>
          </div>
          <div
            className="absolute inset-0 flex items-center justify-center"
          >
            <div
              className="rounded-full p-4 border-2 shadow-lg"
              style={{
                backgroundColor: 'hsl(var(--gb-surface))',
                borderColor: 'hsl(var(--gb-gold))',
                color: 'hsl(var(--gb-navy))',
              }}
            >
              <Lock size={28} strokeWidth={2.25} />
            </div>
          </div>
        </div>

        {/* Unlock CTA */}
        <div
          className="mt-6 rounded-md border-2 px-5 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
          style={{
            borderColor: 'hsl(var(--gb-navy))',
            backgroundColor: 'hsl(var(--gb-surface-2))',
          }}
          data-testid="locked-preview-cta"
        >
          <div className="flex-1">
            <div
              className="font-bold text-lg"
              style={{
                color: 'hsl(var(--gb-navy))',
                fontFamily: 'var(--font-display)',
              }}
            >
              Unlock the full report
            </div>
            <div
              className="text-sm mt-1"
              style={{ color: 'hsl(var(--gb-text-muted))' }}
            >
              Bull/bear bodies, executive summary, geopolitical context, watch list,
              source attribution, and copy-to-clipboard export.
            </div>
            <div
              className="mt-2 flex items-center gap-1.5 text-xs"
              style={{ color: 'hsl(var(--gb-text-muted))' }}
            >
              <ShieldCheck size={14} />
              Secure payment via Stripe · No account required
            </div>
          </div>
          <div className="flex flex-col items-stretch sm:items-end gap-1">
            <button
              type="button"
              onClick={onUnlock}
              disabled={unlocking}
              data-testid="locked-preview-unlock-button"
              className="inline-flex items-center justify-center gap-2 rounded-md px-6 py-3 font-bold text-base shadow-sm transition-transform duration-150 hover:scale-[1.02] active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              style={{
                backgroundColor: 'hsl(var(--gb-navy))',
                color: 'hsl(var(--gb-primary-foreground))',
                fontFamily: 'var(--font-display)',
                ['--tw-ring-color']: 'hsl(var(--gb-gold))',
              }}
            >
              <Lock size={18} />
              {unlocking ? 'Redirecting…' : `Unlock for $${intro}`}
            </button>
            <div
              className="text-xs text-center sm:text-right"
              style={{ color: 'hsl(var(--gb-text-muted))' }}
            >
              Introductory pricing
            </div>
          </div>
        </div>

        {unlockError ? (
          <div
            className="mt-3 rounded-md border px-3 py-2 flex items-start gap-2 text-sm"
            style={{
              borderColor: 'hsl(var(--gb-danger-border))',
              backgroundColor: 'hsl(var(--gb-danger-surface))',
              color: 'hsl(var(--gb-danger-border))',
            }}
            data-testid="locked-preview-error"
          >
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{unlockError}</span>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default LockedPreview;
